import { AxiosResponse } from 'axios';

import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IBaseGist, IPageType } from 'utils/types';

import axiosInstance from '../base';

import {
  normalizeBillingContract,
  normalizeBillingContracts,
  normalizeGatewayInvoice,
  normalizeGatewayInvoiceDetails,
} from './normalizers';
import {
  IBillingContract,
  IBillingContractForm,
  IBillingContracts,
  IBillingContractsData,
  IEditBillingContractPayload,
  IEditGatewayInvoicePayload,
  IGatewayInvoiceCreate,
  IGatewayInvoiceDetails,
  IGatewayInvoiceDetailsData,
  IGatewayInvoices,
  IGatewayInvoicesData,
} from './types';

const invoicesApi = {
  async getAllBillingContracts(
    filters: IAllPageFilters,
  ): Promise<IPageType<IBillingContracts>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('billing-contracts', {
      params: normalizedFilter,
    });

    return normalizePageData<IBillingContractsData, IBillingContracts>(
      response.data,
      normalizeBillingContracts,
      JSON.stringify(filters.search),
    );
  },

  async getBillingContract(id: number | string): Promise<IBillingContract> {
    const response = await axiosInstance.get(`billing-contracts/${id}`);

    return normalizeBillingContract(response?.data);
  },

  async createBillingContract(payload: IBillingContractForm): Promise<IBaseGist> {
    const normalizedBillingContract = prepareFormData(payload);
    const result = await axiosInstance.post(
      `billing-contracts`,
      normalizedBillingContract,
    );
    return normalizeBaseGist(result?.data);
  },

  async editBillingContract({
    id,
    payload,
  }: IEditBillingContractPayload): Promise<IBaseGist> {
    const normalizedBillingContract = prepareFormData(payload);
    const result = await axiosInstance.put(
      `billing-contracts/${id}`,
      normalizedBillingContract,
    );

    return normalizeBaseGist(result.data);
  },

  async deleteBillingContract(id: number): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(`billing-contracts/${id}`);

    return result;
  },

  async getAllGatewayInvoices(
    filters: IAllPageFilters,
  ): Promise<IPageType<IGatewayInvoices>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('gateway-invoices', {
      params: normalizedFilter,
    });

    return normalizePageData<IGatewayInvoicesData, IGatewayInvoices>(
      response.data,
      normalizeGatewayInvoice,
      JSON.stringify(filters.search),
    );
  },

  async getGatewayInvoice(id: string): Promise<IGatewayInvoices> {
    const response = await axiosInstance.get(`gateway-invoices/${id}`);

    return normalizeGatewayInvoice(response?.data);
  },

  async createGatewayInvoice(payload: IGatewayInvoiceCreate): Promise<IBaseGist> {
    const response = await axiosInstance.post(`gateway-invoices`, payload);

    return normalizeBaseGist(response?.data);
  },

  async editGatewayInvoice({
    id,
    payload,
  }: IEditGatewayInvoicePayload): Promise<IBaseGist> {
    const normalizedInfo = prepareFormData(payload);
    const result = await axiosInstance.put(`gateway-invoices/${id}`, normalizedInfo);

    return normalizeBaseGist(result.data);
  },

  async calculateGatewayInvoice({ id }: { id: string }): Promise<IBaseGist> {
    const result = await axiosInstance.patch(`gateway-invoices/${id}`);

    return normalizeBaseGist(result.data);
  },

  async getAllGatewayInvoiceDetails(
    invoiceId: number,
    filters: IAllPageFilters,
  ): Promise<IPageType<IGatewayInvoiceDetails>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('gateway-invoices/details', {
      params: { invoiceId, ...normalizedFilter },
    });

    return normalizePageData<IGatewayInvoiceDetailsData, IGatewayInvoiceDetails>(
      response.data,
      normalizeGatewayInvoiceDetails,
      JSON.stringify(filters.search),
    );
  },

  async deleteGatewayInvoice(id: number): Promise<AxiosResponse> {
    const result = await axiosInstance.delete(`gateway-invoices/${id}`);

    return result;
  },
};

export default invoicesApi;
